import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, DataTable, ColumnSettingsButton, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { useFeatures } from '../../hooks/use-features'
import { useTableColumns } from '../../hooks/use-table-columns'

interface EhrDashboardRow {
  readonly ehrId: string
  readonly ehrName: string
  readonly enabled: boolean
  readonly transportType: string
  readonly lastEventAt: string | null
  readonly counts: { received: number; acked: number; nacked: number }
  readonly errorRate: number
}

function formatDate(dt: string | null): string {
  if (!dt) return '—'
  try {
    return new Date(dt).toLocaleString()
  } catch {
    return dt
  }
}

function ErrorBar({ rate }: { rate: number }) {
  const pct = Math.round(rate * 100)
  const bg = pct > 10 ? '#fecaca' : pct > 0 ? '#fef3c7' : '#d1fae5'
  const color = pct > 10 ? '#991b1b' : pct > 0 ? '#92400e' : '#065f46'
  return (
    <span
      style={{
        display: 'inline-block',
        background: bg,
        color,
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '0.75rem',
        fontWeight: 600,
      }}
    >
      {pct}%
    </span>
  )
}

export function EhrDashboardPage() {
  const navigate = useNavigate()
  const features = useFeatures()
  const [rows, setRows] = useState<readonly EhrDashboardRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('ehr-dashboard')

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly EhrDashboardRow[]>('/admin/ehr-dashboard')
      .then((res) => {
        if (cancelled) return
        setRows(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load EHR dashboard')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const isCentral = !!features.remote_access

  const columns: readonly Column<EhrDashboardRow>[] = [
    {
      key: 'ehrName',
      header: 'EHR Name',
      sortable: true,
      render: (r) => <strong style={{ fontSize: '0.875rem' }}>{r.ehrName}</strong>,
    },
    {
      key: 'transportType',
      header: 'Transport',
      render: (r) => (
        <code style={{ fontSize: '0.8125rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
          {r.transportType}
        </code>
      ),
    },
    {
      key: 'enabled',
      header: 'Status',
      render: (r) => <StatusBadge status={r.enabled ? 'active' : 'inactive'} />,
    },
    {
      key: 'lastEventAt',
      header: 'Last Activity',
      sortable: true,
      render: (r) => formatDate(r.lastEventAt),
    },
    {
      key: 'counts',
      header: 'Messages (24h)',
      render: (r) => (
        <span style={{ fontSize: '0.8125rem' }}>
          {r.counts.received + r.counts.acked + r.counts.nacked === 0
            ? '—'
            : `${r.counts.received} recv / ${r.counts.acked} ack / ${r.counts.nacked} err`}
        </span>
      ),
    },
    {
      key: 'errorRate',
      header: 'Error Rate',
      sortable: true,
      render: (r) =>
        r.counts.received + r.counts.acked + r.counts.nacked === 0 ? (
          <span style={{ color: colors.textMuted }}>—</span>
        ) : (
          <ErrorBar rate={r.errorRate} />
        ),
    },
  ]

  const handleRowClick = (row: EhrDashboardRow) => {
    navigate(`/settings/ehr/${row.ehrId}`)
  }

  const logLinkBase = isCentral ? '/fleet/logs?evt=hl7.*' : '/admin/logs?evt=hl7.*'

  return (
    <div style={{ padding: '1.5rem' }}>
      <PageHeader
        title="EHR Integrations"
        subtitle="Live status of configured EHR integrations"
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <a
              href={logLinkBase}
              style={{
                fontSize: '0.8125rem',
                color: colors.primary,
                textDecoration: 'none',
              }}
            >
              View HL7 Message Log
            </a>
          </>
        }
      />

      {error && (
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '0.625rem 0.875rem',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            color: '#991b1b',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows as EhrDashboardRow[]}
          keyField="ehrId"
          emptyMessage="No EHR integrations configured"
          onRowClick={handleRowClick}
          {...tableColumnProps}
        />
      )}
    </div>
  )
}
